import {ConfigRoute, ConfigTypes} from "~/config.types";

export enum Slug{
    Login = "login",
    Logout = "logout",
    Signup = "signup",
    Restore = "restore",
    Index = "index",
    Docs = "docs",
    DocsReference = Docs + "-reference",
    DocsGuides = Docs + "-guides",
    DocsChangelog = Docs + "-changelog",
    Api = "api",
    Invite = "invite",
    Verify = "verify",
    Oauth = "oauth",
    Playground = "playground",
    Dashboard = "dashboard",
    DashboardAccount = Dashboard + "-account",
    DashboardAccountAuth = DashboardAccount + "-auth",
    DashboardAccountSessions = DashboardAccount + "-sessions",
    DashboardStatistics = Dashboard + "-statistics",
    DashboardStatisticsUsage = DashboardStatistics + "-usage",
    DashboardStatisticsRequests = DashboardStatistics + "-requests",
    DashboardLog = Dashboard + "-log",
    DashboardAdmin = Dashboard + "-admin",
    DashboardAdminInvites = DashboardAdmin + "-invites",
}

export enum Group{
    Dashboard = "dashboard",
}

function prepareSlugs(pages: ConfigTypes["routes"], prefixSlug: string = "", parent?: ConfigRoute): ConfigTypes["routes"] {
    return Object.keys(pages).reduce(
        (prepPages, slug) => {
            const page = pages[slug];
            const baseSlug = slug;
            if (slug.startsWith(`${prefixSlug}-`)) {
                slug = slug.substring(prefixSlug.length + 1, slug.length);
            }

            prepPages[slug] = {
                ...page,
                group: page.group || parent && parent.group || "",
                authRequired: page.authRequired || parent && parent.authRequired || false,
                slug
            };

            if (page.children) {
                prepPages[slug].children = prepareSlugs(page.children, baseSlug, prepPages[slug]);
            }

            return prepPages;
        },
        {}
    );
}

const config: ConfigTypes = {
    routes: prepareSlugs({
        [Slug.Index]: {
            path: "/",
            title: "Main page",
            editable: false
        },
        [Slug.Docs]: {
            title: "Docs",
            splat: true,
            children: {
                [Slug.DocsReference]: {
                    title: "API References",
                    template: "Docs",
                },
                [Slug.DocsGuides]: {
                    title: "Guides",
                    splat: true,
                    template: "Docs",
                },
                [Slug.DocsChangelog]: {
                    title: "Changelog",
                    template: "Docs",
                }
            }
        },
        [Slug.Api]: {
            title: "API",
            editable: false
        },
        [Slug.Login]: {
            title: "Sign-In",
            editable: false
        },
        [Slug.Logout]: {
            title: "Log out",
            editable: false
        },
        [Slug.Signup]: {
            title: "Sign-Up",
            editable: false
        },
        [Slug.Restore]: {
            title: "Password restore",
            editable: false
        },
        [Slug.Invite]: {
            title: "Invite for:{{.email}} id:{{.invite-id}}",
            params: {"invite-id":{}}
        },
        [Slug.Verify]: {
            title: "Signup verification token:{{.verify-token}}",
            params: {"verify-token":{}}
        },
        [Slug.Oauth]: {
            title: "OAuth authentication: {{.provider}}",
            params: {"provider":{optional: false}}
        },
        [Slug.Playground]: {
            title: "Try our API",
            template: "Index",
        },
        [Slug.Dashboard]: {
            title: "Dashboard",
            authRequired: true,
            group: Group.Dashboard,
            children: {
                [Slug.DashboardAccount]: {
                    title: {
                        default: "Account",
                        editable: "Account management",
                    },
                    ico: "User",
                    editable: true,
                    children: {
                        [Slug.DashboardAccountAuth]: {title: "Authentication"},
                        [Slug.DashboardAccountSessions]: {title: "Sessions"},
                    }
                },
                [Slug.DashboardStatistics]: {
                    title: "Statistics",
                    ico: "LineChart",
                    children: {
                        [Slug.DashboardStatisticsUsage]: {title: "Usage"},
                        [Slug.DashboardStatisticsRequests]: {title: "Requests"},
                    }
                },
                [Slug.DashboardLog]: {
                    title: "Request Log",
                    ico: "Calendar",
                },
                [Slug.DashboardAdmin]: {
                    title: "Admin",
                    ico: "Lock",
                    children: {[Slug.DashboardAdminInvites]: {title: "Invites"}}
                },
            },
        },
        "*": {
            title: "Unknown page",
            template: "404"
        }
    })
};

export default config;